import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import type { ImageRecord } from '../utils/imageDb'

export const useTransientUploadStore = defineStore('transientUploadStore', () => {
  const records = ref<Record<string, ImageRecord>>({})

  // Merge a single record into the map IN PLACE. Vue 3 deep-reactivity tracks
  // key additions/updates on the proxied object, so we avoid allocating and
  // spreading the entire map on every upsert (was O(n) per call → now O(1)).
  function mergeInto(target: Record<string, ImageRecord>, rec: ImageRecord) {
    const id = String((rec as any)?.id || '').trim()
    if (!id) return
    target[id] = {
      ...(target[id] || {}),
      ...(rec as any),
      upload: {
        ...(((target[id] as any)?.upload || {}) as any),
        ...(((rec as any)?.upload || {}) as any),
      },
      edits: {
        ...(((target[id] as any)?.edits || {}) as any),
        ...(((rec as any)?.edits || {}) as any),
      },
    } as ImageRecord
  }

  function upsert(rec: ImageRecord) {
    mergeInto(records.value, rec)
  }

  function upsertMany(items: ImageRecord[]) {
    for (const rec of Array.isArray(items) ? items : []) mergeInto(records.value, rec)
  }

  function patch(idRaw: string, patchValue: Partial<ImageRecord>) {
    const id = String(idRaw || '').trim()
    if (!id) return
    const current = records.value[id]
    if (!current) return
    upsert({
      ...(current as any),
      ...(patchValue as any),
      upload: {
        ...((current as any)?.upload || {}),
        ...((patchValue as any)?.upload || {}),
      },
      edits: {
        ...((current as any)?.edits || {}),
        ...((patchValue as any)?.edits || {}),
      },
    } as ImageRecord)
  }

  function remove(idRaw: string) {
    const id = String(idRaw || '').trim()
    if (!id || !records.value[id]) return
    delete records.value[id]
  }

  // --- Bounded cleanup ---------------------------------------------------
  // The records map holds full ImageRecords (including blob data) and otherwise
  // only grows for the lifetime of the page. The following actions let callers
  // drop records they no longer need so memory can be reclaimed. Both perform a
  // id (in place) so memory can be reclaimed.

  function removeMany(idsRaw: Array<string | number>) {
    const ids = (Array.isArray(idsRaw) ? idsRaw : [])
      .map((x) => String(x || '').trim())
      .filter(Boolean)
    if (!ids.length) return
    for (const id of ids) {
      if (records.value[id]) delete records.value[id]
    }
  }

  // Keep only the records whose id is in keepIds; drop everything else.
  function prune(keepIdsRaw: Array<string | number>) {
    const keep = new Set(
      (Array.isArray(keepIdsRaw) ? keepIdsRaw : [])
        .map((x) => String(x || '').trim())
        .filter(Boolean),
    )
    for (const id of Object.keys(records.value)) {
      if (!keep.has(id)) delete records.value[id]
    }
  }

  function get(idRaw: string): ImageRecord | null {
    const id = String(idRaw || '').trim()
    return id ? (records.value[id] || null) : null
  }

  function listByProjectCode(codeRaw: string): ImageRecord[] {
    const code = String(codeRaw || '').trim()
    return Object.values(records.value)
      .filter((rec: any) => String(rec?.projectCode || '').trim() === code)
      .sort((a, b) => Number(b?.createdAt || 0) - Number(a?.createdAt || 0))
  }

  function listByCategoryId(categoryIdRaw: string | number): ImageRecord[] {
    const categoryId = Number(categoryIdRaw || 0)
    if (!(categoryId > 0)) return []
    return Object.values(records.value)
      .filter((rec: any) => Number(rec?.categoryId || 0) === categoryId)
      .sort((a, b) => Number(b?.createdAt || 0) - Number(a?.createdAt || 0))
  }

  const all = computed(() =>
    Object.values(records.value).sort((a, b) => Number(b?.createdAt || 0) - Number(a?.createdAt || 0)),
  )

  return {
    all,
    upsert,
    upsertMany,
    patch,
    remove,
    removeMany,
    prune,
    get,
    listByProjectCode,
    listByCategoryId,
  }
})
